import { lockPageScroll } from '../../../assets/js/utils/scroll-lock';
import { ProductSearchRequest, normalizeSearch, readSearchHistory, rememberSearch, clearSearchHistory } from '../../../assets/js/utils/product-search.mjs';

let storage;
try { storage = window.localStorage; } catch { /* Search does not require browser storage. */ }
const unmodified = (event) => !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

document.querySelectorAll('.ProductSearchForm').forEach((form) => {
    form.addEventListener('submit', () => {
        const input = form.elements.s;
        input.value = normalizeSearch(input.value);
        rememberSearch(storage, input.value);
    });
});

class SearchDialog {
    constructor(dialog) {
        this.dialog = dialog;
        this.config = JSON.parse(dialog.dataset.searchConfig);
        this.input = dialog.querySelector('[data-search-input]');
        this.form = dialog.querySelector('[data-search-form]');
        this.results = dialog.querySelector('[data-search-results]');
        this.status = dialog.querySelector('[data-search-status]');
        this.body = dialog.querySelector('[data-search-body]');
        this.clear = dialog.querySelector('[data-search-clear]');
        this.initial = this.results.innerHTML;
        this.triggers = [...document.querySelectorAll('[data-search-open]')];
        this.request = new ProductSearchRequest({ ...this.config, onState: (state) => this.render(state) });
        this.viewport = () => {
            const viewport = window.visualViewport;
            if (!viewport || !dialog.open) return;
            dialog.style.setProperty('--search-viewport-height', `${viewport.height}px`);
            dialog.style.setProperty('--search-viewport-top', `${viewport.offsetTop}px`);
            dialog.classList.toggle('is-compact', viewport.width < 768 && viewport.height < 500);
        };
        this.bind();
        this.updateTriggers();
    }

    url(query) {
        const url = new URL(this.form.action);
        url.searchParams.set('s', normalizeSearch(query));
        url.searchParams.set('post_type', 'product');
        return url.toString();
    }

    bind() {
        this.triggers.forEach((trigger) => trigger.addEventListener('click', (event) => {
            if (!unmodified(event)) return;
            event.preventDefault();
            this.open(trigger);
        }));
        this.dialog.querySelector('[data-search-close]').addEventListener('click', () => this.close());
        this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
        this.dialog.addEventListener('close', () => this.afterClose());
        const outside = (event) => {
            const box = this.dialog.getBoundingClientRect();
            return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
        };
        this.dialog.addEventListener('pointerdown', (event) => { this.backdropDown = event.target === this.dialog && outside(event); });
        this.dialog.addEventListener('click', (event) => {
            if (this.backdropDown && event.target === this.dialog && outside(event)) this.close();
            this.backdropDown = false;
            const suggestion = event.target.closest('[data-search-query]');
            if (suggestion && unmodified(event)) {
                event.preventDefault();
                this.input.value = suggestion.dataset.searchQuery;
                this.input.focus({ preventScroll: true });
                this.search(true);
            }
            if (event.target.closest('[data-search-retry]')) this.search(true);
            if (event.target.closest('.SearchResults a[href]')) rememberSearch(storage, this.input.value);
        });
        this.input.addEventListener('compositionstart', () => { this.composing = true; this.request.cancel(); });
        this.input.addEventListener('compositionend', () => { this.composing = false; this.search(); });
        this.input.addEventListener('input', (event) => { if (!event.isComposing && !this.composing) this.search(); });
        this.input.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowDown' || event.isComposing || this.composing) return;
            const first = this.results.querySelector('a[href]');
            if (first) { event.preventDefault(); first.focus(); }
        });
        this.clear.addEventListener('click', () => {
            this.input.value = '';
            this.input.focus({ preventScroll: true });
            this.search();
        });
        this.dialog.querySelector('[data-search-history-clear]').addEventListener('click', () => {
            clearSearchHistory(storage);
            this.history();
            this.input.focus({ preventScroll: true });
        });
        this.form.addEventListener('submit', (event) => { if (this.composing) event.preventDefault(); });
        window.addEventListener('pageshow', () => { if (this.dialog.open) this.search(true); });
    }

    open(trigger) {
        if (this.dialog.open) return;
        this.trigger = trigger;
        this.release = lockPageScroll();
        this.dialog.showModal();
        this.history();
        this.updateTriggers();
        window.visualViewport?.addEventListener('resize', this.viewport);
        window.visualViewport?.addEventListener('scroll', this.viewport);
        this.viewport();
        this.input.focus({ preventScroll: true });
        this.search(true);
    }

    close() {
        if (this.closing || !this.dialog.open) return;
        this.closing = true;
        this.request.cancel();
        const finish = () => { this.dialog.close(); };
        if (!this.dialog.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
        this.animation = this.dialog.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(8px)' }], { duration: 160, easing: 'ease-in', fill: 'forwards' });
        this.animation.finished.then(finish, finish);
    }

    afterClose() {
        this.request.cancel();
        this.animation?.cancel();
        this.animation = null;
        this.closing = false;
        this.release?.();
        this.release = null;
        window.visualViewport?.removeEventListener('resize', this.viewport);
        window.visualViewport?.removeEventListener('scroll', this.viewport);
        this.updateTriggers();
        this.trigger?.focus({ preventScroll: true });
    }

    updateTriggers() { this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', String(this.dialog.open))); }

    search(immediate = false) {
        this.clear.hidden = this.input.value.length === 0;
        this.request.search(this.input.value, immediate);
    }

    history() {
        const values = readSearchHistory(storage);
        this.dialog.querySelector('[data-search-history]').hidden = values.length === 0;
        const list = this.dialog.querySelector('[data-search-history-list]');
        list.replaceChildren(...values.map((value) => {
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = this.url(value);
            link.dataset.searchQuery = value;
            link.textContent = value;
            item.append(link);
            return item;
        }));
        this.dialog.toggleAttribute('data-search-sidebar', !!this.dialog.querySelector('.SearchDialog__aside a[href]'));
    }

    render({ state, query, data }) {
        if (!this.dialog.open || this.closing) return;
        this.dialog.toggleAttribute('data-search-active', state !== 'initial');
        this.results.setAttribute('aria-busy', String(state === 'loading'));
        if (state === 'initial') {
            this.results.innerHTML = this.initial;
            this.status.textContent = query ? this.config.tooShort : '';
        } else if (state === 'success') {
            this.results.innerHTML = data.html;
            this.status.textContent = data.announcement;
        } else {
            this.results.replaceChildren(this.dialog.querySelector(`[data-search-${state}]`).content.cloneNode(true));
            this.status.textContent = this.config[state];
            const link = this.results.querySelector('[data-search-fallback]');
            if (link) link.href = this.url(query);
        }
        this.body.scrollTo({ top: 0, behavior: 'instant' });
    }
}

const dialog = document.getElementById('product-search');
if (typeof HTMLDialogElement !== 'undefined' && dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
    new SearchDialog(dialog);
}
